import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import styles from './CouponAdmin.module.css';

type Coupon = {
  id: number;
  code: string;
  description?: string | null;
  partner_name?: string | null;
  discount_type: 'free' | 'percent' | 'fixed';
  discount_value?: string | number | null;
  product: string;
  max_uses?: number | null;
  current_uses: number;
  redemption_count: number;
  expires_at?: string | null;
  is_active: boolean;
  created_at?: string;
};

type Redemption = {
  id: number;
  code: string;
  customer_email?: string | null;
  product?: string | null;
  order_id?: string | null;
  referred_by_code?: string | null;
  referred_by_type?: string | null;
  redeemed_at: string;
  source?: string;
};

type FormData = {
  code: string;
  description: string;
  partner_name: string;
  discount_type: Coupon['discount_type'];
  discount_value: string;
  product: string;
  max_uses: string;
  expires_at: string;
  is_active: boolean;
};

const emptyForm: FormData = {
  code: '',
  description: '',
  partner_name: '',
  discount_type: 'free',
  discount_value: '',
  product: 'all',
  max_uses: '',
  expires_at: '',
  is_active: true,
};

type FetchOptions = { method?: string; headers?: Record<string, string>; body?: string };

async function adminFetch<T>(
  user: ReturnType<typeof useAuth>['currentUser'],
  url: string,
  options: FetchOptions = {}
): Promise<T> {
  if (!user) throw new Error('You must be signed in as an administrator.');
  const token = await user.getIdToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
  return data as T;
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '—';
}

function discountLabel(coupon: Coupon) {
  if (coupon.discount_type === 'free') return 'Free';
  return coupon.discount_type === 'percent'
    ? `${coupon.discount_value}%`
    : `$${coupon.discount_value}`;
}

export default function CouponAdmin() {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormData>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const couponsQuery = useQuery({
    queryKey: ['admin-coupons'],
    queryFn: () => adminFetch<Coupon[]>(currentUser, '/api/coupons'),
    enabled: Boolean(currentUser),
  });
  const reportQuery = useQuery({
    queryKey: ['admin-referral-report'],
    queryFn: () => adminFetch<Redemption[]>(currentUser, '/api/coupons/redemptions'),
    enabled: Boolean(currentUser),
  });
  const redemptionsQuery = useQuery({
    queryKey: ['coupon-redemptions', selectedCoupon?.id],
    queryFn: () =>
      adminFetch<Redemption[]>(currentUser, `/api/coupons/${selectedCoupon!.id}/redemptions`),
    enabled: Boolean(selectedCoupon),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      adminFetch<Coupon>(currentUser, editingId ? `/api/coupons/${editingId}` : '/api/coupons', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify({
          ...form,
          discount_value: form.discount_type === 'free' ? null : form.discount_value,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
      setMessage(editingId ? 'Coupon updated.' : 'Coupon created.');
      setError(null);
      resetForm();
    },
    onError: err => {
      setError(err.message);
      setMessage(null);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminFetch(currentUser, `/api/coupons/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
      setMessage('Coupon deleted.');
      setError(null);
    },
    onError: err => {
      setError(err.message);
      setMessage(null);
    },
  });

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
  }
  function editCoupon(coupon: Coupon) {
    setEditingId(coupon.id);
    setForm({
      code: coupon.code,
      description: coupon.description || '',
      partner_name: coupon.partner_name || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value == null ? '' : String(coupon.discount_value),
      product: coupon.product,
      max_uses: coupon.max_uses == null ? '' : String(coupon.max_uses),
      expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 16) : '',
      is_active: coupon.is_active,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm(previous => ({ ...previous, [key]: value }));
  }

  if (!currentUser)
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>Coupon Management</h1>
        <p>Sign in as an administrator to continue.</p>
      </div>
    );

  return (
    <main className={styles.container}>
      <h1 className={styles.title}>Coupon Management</h1>
      <p className={styles.subtitle}>
        Manage discounts and see exactly which code referred each purchase.
      </p>
      {message && <div className={styles.success}>{message}</div>}
      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.panel}>
        <h2>{editingId ? 'Edit Coupon' : 'Create Coupon'}</h2>
        <form
          className={styles.form}
          onSubmit={event => {
            event.preventDefault();
            saveMutation.mutate();
          }}
        >
          <label>
            Code
            <input
              required
              disabled={Boolean(editingId)}
              value={form.code}
              onChange={e => updateField('code', e.target.value.toUpperCase())}
              placeholder="PARTNER2026"
            />
          </label>
          <label>
            Description
            <input
              value={form.description}
              onChange={e => updateField('description', e.target.value)}
            />
          </label>
          <label>
            Partner or MPD user
            <input
              value={form.partner_name}
              onChange={e => updateField('partner_name', e.target.value)}
            />
          </label>
          <label>
            Discount type
            <select
              value={form.discount_type}
              onChange={e =>
                updateField('discount_type', e.target.value as Coupon['discount_type'])
              }
            >
              <option value="free">Free</option>
              <option value="percent">Percent</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </label>
          <label>
            Discount value
            <input
              type="number"
              min="0"
              max={form.discount_type === 'percent' ? 100 : undefined}
              step="0.01"
              disabled={form.discount_type === 'free'}
              value={form.discount_value}
              onChange={e => updateField('discount_value', e.target.value)}
            />
          </label>
          <label>
            Product
            <select value={form.product} onChange={e => updateField('product', e.target.value)}>
              <option value="all">All products</option>
              <option value="desktop">Desktop</option>
              <option value="mpdId">MPD ID</option>
            </select>
          </label>
          <label>
            Maximum uses
            <input
              type="number"
              min="1"
              step="1"
              value={form.max_uses}
              onChange={e => updateField('max_uses', e.target.value)}
              placeholder="Unlimited"
            />
          </label>
          <label>
            Expires at
            <input
              type="datetime-local"
              value={form.expires_at}
              onChange={e => updateField('expires_at', e.target.value)}
            />
          </label>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => updateField('is_active', e.target.checked)}
            />{' '}
            Active
          </label>
          <div className={styles.actions}>
            <button className={styles.primary} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : editingId ? 'Update coupon' : 'Create coupon'}
            </button>
            {editingId && (
              <button type="button" className={styles.secondary} onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className={styles.panel}>
        <div className={styles.sectionHeader}>
          <h2>Coupons</h2>
          <button className={styles.secondary} onClick={() => couponsQuery.refetch()}>
            Refresh
          </button>
        </div>
        {couponsQuery.isLoading ? (
          <p>Loading coupons…</p>
        ) : couponsQuery.isError ? (
          <p className={styles.error}>Failed to load coupons.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Partner</th>
                  <th>Discount</th>
                  <th>Product</th>
                  <th>Uses</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(couponsQuery.data || []).map(coupon => (
                  <tr key={coupon.id}>
                    <td>
                      <strong>{coupon.code}</strong>
                      <small>{coupon.description || ''}</small>
                    </td>
                    <td>{coupon.partner_name || '—'}</td>
                    <td>{discountLabel(coupon)}</td>
                    <td>{coupon.product}</td>
                    <td>
                      {coupon.current_uses} / {coupon.max_uses ?? '∞'}
                    </td>
                    <td>{formatDate(coupon.expires_at)}</td>
                    <td>
                      <span className={coupon.is_active ? styles.active : styles.inactive}>
                        {coupon.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className={styles.rowActions}>
                      <button onClick={() => editCoupon(coupon)}>Edit</button>
                      <button onClick={() => setSelectedCoupon(coupon)}>Redemptions</button>
                      <button
                        className={styles.danger}
                        onClick={() => {
                          if (window.confirm(`Delete ${coupon.code}?`))
                            deleteMutation.mutate(coupon.id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {couponsQuery.data?.length === 0 && <p>No coupons created yet.</p>}
          </div>
        )}
      </section>

      <section className={styles.panel}>
        <div className={styles.sectionHeader}>
          <h2>Referral Report</h2>
          <button className={styles.secondary} onClick={() => reportQuery.refetch()}>
            Refresh
          </button>
        </div>
        <p className={styles.hint}>
          Every redemption and paid referral captured from the Buy flow.
        </p>
        {reportQuery.isLoading ? (
          <p>Loading report…</p>
        ) : (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Order</th>
                  <th>Recorded</th>
                </tr>
              </thead>
              <tbody>
                {(reportQuery.data || []).map(row => (
                  <tr key={`${row.source}-${row.id}`}>
                    <td>{row.referred_by_code || row.code}</td>
                    <td>{row.referred_by_type || row.source || '—'}</td>
                    <td>{row.customer_email || '—'}</td>
                    <td>{row.product || '—'}</td>
                    <td>{row.order_id || 'Free redemption'}</td>
                    <td>{formatDate(row.redeemed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reportQuery.data?.length === 0 && <p>No referrals recorded yet.</p>}
          </div>
        )}
      </section>

      {selectedCoupon && (
        <div className={styles.modalBackdrop} onClick={() => setSelectedCoupon(null)}>
          <section className={styles.modal} onClick={event => event.stopPropagation()}>
            <div className={styles.sectionHeader}>
              <h2>{selectedCoupon.code} redemptions</h2>
              <button className={styles.secondary} onClick={() => setSelectedCoupon(null)}>
                Close
              </button>
            </div>
            {redemptionsQuery.isLoading ? (
              <p>Loading…</p>
            ) : (
              <div className={styles.tableWrap}>
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Product</th>
                      <th>Referred by</th>
                      <th>Order</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(redemptionsQuery.data || []).map(row => (
                      <tr key={row.id}>
                        <td>{row.customer_email || '—'}</td>
                        <td>{row.product || '—'}</td>
                        <td>{row.referred_by_code || '—'}</td>
                        <td>{row.order_id || 'Free redemption'}</td>
                        <td>{formatDate(row.redeemed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
